import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Settings, DollarSign, Users, Save, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export default function AdminSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  const backendUrl = getBackendUrl();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${backendUrl}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      
      // Initialize edited values with current values
      const initialValues = {};
      response.data.forEach(setting => {
        initialValues[setting.key] = setting.value;
      });
      setEditedValues(initialValues);
      setHasChanges(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      if (error.response?.status === 404 || error.response?.data?.detail?.includes('not found')) {
        // Settings table might not exist yet, try to seed
        await seedSettings();
      } else {
        toast.error('Failed to load settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const seedSettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${backendUrl}/api/settings/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Default settings created');
      await fetchSettings();
    } catch (error) {
      console.error('Error seeding settings:', error);
      toast.error('Failed to initialize settings');
    }
  };

  const handleValueChange = (key, value) => {
    setEditedValues(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      
      await axios.put(`${backendUrl}/api/settings`, {
        settings: editedValues
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Settings saved successfully');
      setHasChanges(false);
      await fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const initialValues = {};
    settings.forEach(setting => {
      initialValues[setting.key] = setting.value;
    });
    setEditedValues(initialValues);
    setHasChanges(false);
  };

  const groupedSettings = settings.reduce((acc, setting) => {
    if (!acc[setting.category]) {
      acc[setting.category] = [];
    }
    acc[setting.category].push(setting);
    return acc;
  }, {});

  const renderSettingInput = (setting) => {
    const value = editedValues[setting.key] ?? setting.value;
    
    switch (setting.value_type) {
      case 'boolean':
        return (
          <Switch
            checked={value === 'true' || value === true}
            onCheckedChange={(checked) => handleValueChange(setting.key, checked ? 'true' : 'false')}
          />
        );
      case 'number':
        return (
          <div className="flex items-center gap-2">
            {setting.key.includes('amount') || setting.key.includes('discount') ? (
              <span className="text-muted-foreground">$</span>
            ) : null}
            <Input
              type="number"
              value={value}
              onChange={(e) => handleValueChange(setting.key, e.target.value)}
              className="w-32"
              min="0"
              step="0.01"
            />
          </div>
        );
      default:
        return (
          <Input
            type="text"
            value={value}
            onChange={(e) => handleValueChange(setting.key, e.target.value)}
            className="w-64"
          />
        );
    }
  };

  const formatSettingLabel = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'referral':
        return <Users className="h-5 w-5" />;
      default:
        return <Settings className="h-5 w-5" />;
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <DashboardLayout role={user?.role} title="Settings" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-destructive" />
            <p className="mt-4 text-lg font-medium">Access Denied</p>
            <p className="text-muted-foreground">Admin access required</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Settings" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={user.role} title="Settings" user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Settings className="h-6 w-6" />
              Platform Settings
            </h2>
            <p className="text-muted-foreground">Configure application-wide settings</p>
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <Badge variant="outline" className="text-warning border-warning">
                Unsaved changes
              </Badge>
            )}
            <Button variant="outline" onClick={handleReset} disabled={!hasChanges || saving}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Reset
            </Button>
            <Button onClick={handleSaveAll} disabled={!hasChanges || saving}>
              {saving ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Settings Tabs */}
        {Object.keys(groupedSettings).length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Settings className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No settings found</p>
              <p className="text-muted-foreground mb-4">Initialize default settings to get started</p>
              <Button onClick={seedSettings}>
                Initialize Settings
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue={Object.keys(groupedSettings)[0]} className="space-y-4">
            <TabsList>
              {Object.keys(groupedSettings).map(category => (
                <TabsTrigger key={category} value={category} className="capitalize flex items-center gap-2">
                  {getCategoryIcon(category)}
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>

            {Object.entries(groupedSettings).map(([category, categorySettings]) => (
              <TabsContent key={category} value={category}>
                <Card>
                  <CardHeader>
                    <CardTitle className="capitalize flex items-center gap-2">
                      {getCategoryIcon(category)}
                      {category} Settings
                    </CardTitle>
                    <CardDescription>
                      {category === 'referral' 
                        ? 'Configure referral program rewards and bonuses'
                        : `Manage ${category} configuration options`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {categorySettings.map(setting => (
                      <div key={setting.key} className="flex items-center justify-between py-4 border-b last:border-0">
                        <div className="space-y-1">
                          <Label className="text-base font-medium">
                            {formatSettingLabel(setting.key)}
                          </Label>
                          {setting.description && (
                            <p className="text-sm text-muted-foreground">
                              {setting.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          {renderSettingInput(setting)}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Category-specific info cards */}
                {category === 'referral' && (
                  <Card className="mt-4 bg-primary/5 border-primary/20">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Referral Program Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="text-center p-3 bg-background rounded-lg">
                          <p className="text-2xl font-bold text-primary">
                            ${editedValues['referral_bonus_amount'] || '50'}
                          </p>
                          <p className="text-sm text-muted-foreground">Referrer Bonus</p>
                        </div>
                        <div className="text-center p-3 bg-background rounded-lg">
                          <p className="text-2xl font-bold text-primary">
                            ${editedValues['referral_referee_discount'] || '25'}
                          </p>
                          <p className="text-sm text-muted-foreground">Referee Discount</p>
                        </div>
                        <div className="text-center p-3 bg-background rounded-lg">
                          <p className="text-2xl font-bold text-primary flex items-center justify-center gap-1">
                            {editedValues['referral_program_enabled'] === 'true' ? (
                              <><CheckCircle2 className="h-5 w-5 text-green-500" /> Active</>
                            ) : (
                              <><AlertCircle className="h-5 w-5 text-red-500" /> Inactive</>
                            )}
                          </p>
                          <p className="text-sm text-muted-foreground">Program Status</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}
